import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ActionDialog from "../../components/ActionDialog";

const RFQDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as any;
  const rfq = state?.rfq || {};

  const [openReject, setOpenReject] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [reason, setReason] = useState("");
  const [editQty, setEditQty] = useState<number>(rfq?.quantity || 0);
  const [actionAnchor, setActionAnchor] = useState<null | HTMLElement>(null);

  const approve = () => {
    // UI-only
    navigate("/rfq", { replace: true });
  };

  const saveReject = () => {
    setOpenReject(false);
    navigate("/rfq", { replace: true });
  };
  const saveEdit = () => {
    setOpenEdit(false);
  };
  const saveInfo = () => {
    setOpenInfo(false);
  };

  const openActions = (e: React.MouseEvent<HTMLElement>) => setActionAnchor(e.currentTarget);
  const closeActions = () => setActionAnchor(null);

  return (
    <>
      <div className="main_title">
        <h1>RFQ Detail</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - {" "}
          <button onClick={() => navigate("/rfq")}>RFQs</button> - Detail
        </p>
      </div>

      <div className="cards view_page">
        <div className="table_header">
          <h2>Request Summary</h2>
          <div className="right_s">
            <Button className="btn btn_outline sm" onClick={openActions} endIcon={<MoreVertIcon />}>Actions</Button>
            <Menu
              anchorEl={actionAnchor}
              open={Boolean(actionAnchor)}
              onClose={closeActions}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { approve(); closeActions(); }}>Approve</MenuItem>
              <MenuItem onClick={() => { setReason(""); setOpenReject(true); closeActions(); }}>Reject</MenuItem>
              <MenuItem onClick={() => { setOpenEdit(true); closeActions(); }}>Edit Scope</MenuItem>
              <MenuItem onClick={() => { setReason(""); setOpenInfo(true); closeActions(); }}>Request More Info</MenuItem>
            </Menu>
          </div>
        </div>
        <div className="gap_p">
          <div className="w_33">
            <h6>Customer</h6>
            <p>{rfq?.customer || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Project</h6>
            <p>{rfq?.project || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Date</h6>
            <p>{rfq?.date || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Product</h6>
            <p>{rfq?.product || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Quantity</h6>
            <p>{rfq?.quantity ?? '-'}</p>
          </div>
          <div className="w_33">
            <h6>Grade</h6>
            <p>{rfq?.grade || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Delivery Address</h6>
            <p>{rfq?.deliveryAddress || '-'}</p>
          </div>
        </div>
      </div>

      <div className="cards">
        <div className="table_header">
          <h2>Attachments</h2>
        </div>
        {(rfq?.attachments && rfq.attachments.length) ? (
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rfq.attachments.map((att: string, idx: number) => {
                  const name = att?.split('/').pop() || `Attachment ${idx + 1}`;
                  return (
                    <TableRow key={`${att}-${idx}`} hover>
                      <TableCell>{name}</TableCell>
                      <TableCell>
                        <a href={att} target="_blank" rel="noopener noreferrer" className="btn btn_outline sm">View</a>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 2, color: '#64748b' }}>No attachments</Box>
        )}
      </div>

      <div className="cards">
        <div className="table_header">
          <h2>Linked</h2>
        </div>
        <div className="gap_p">
          <div className="w_33">
            <h6>Linked Customer</h6>
            <p>{rfq?.customer || '-'}</p>
          </div>
          <div className="w_33">
            <h6>Linked Project</h6>
            <p>{rfq?.project || '-'}</p>
          </div>
        </div>
      </div>

      {/* Reject Dialog */}
      <ActionDialog
        open={openReject}
        onClose={() => setOpenReject(false)}
        title="Reject RFQ"
        helperText="Provide a brief reason for rejecting this RFQ."
        confirmLabel="Reject"
        confirmDisabled={!reason.trim()}
        onConfirm={saveReject}
        confirmButtonProps={{ className: "btn btn_secondary" }}
      >
        <TextField
          label="Reason"
          placeholder="Enter rejection reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>

      {/* Edit Scope Dialog */}
      <ActionDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        title="Edit Scope"
        helperText="Change the requested quantity if needed."
        confirmLabel="Save"
        onConfirm={saveEdit}
        confirmButtonProps={{ className: "btn btn_primary" }}
      >
        <TextField
          type="number"
          label="Quantity"
          value={editQty}
          onChange={(e) => setEditQty(parseInt(e.target.value || '0', 10))}
          fullWidth
          size="small"
        />
      </ActionDialog>

      {/* Request More Info Dialog */}
      <ActionDialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title="Request More Info"
        helperText="Send guidance on what information is missing."
        confirmLabel="Send"
        confirmDisabled={!reason.trim()}
        onConfirm={saveInfo}
        confirmButtonProps={{ className: "btn btn_primary" }}
      >
        <TextField
          label="Message"
          placeholder="Ask for missing information"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          size="small"
          sx={{
            '& textarea': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.875rem',
              letterSpacing: '0.3px',
              padding: '10px'
            }
          }}
        />
      </ActionDialog>
    </>
  );
};

export default RFQDetailPage;
